import { useEffect, useContext, useRef } from "react";
import { DataContext } from "../context/DataContext";
import { getEdgeStatusUrl } from "../utils/edge";

const useEdgeStatusCheck = (triggerCondition, customHandler = null, isHandoff = false) => {
  const interval = 2000; // 2 seconds polling interval
  const { settings, dispatch } = useContext(DataContext);
  const isRequestPendingRef = useRef(false); // Track if a request is in progress

  useEffect(() => {
    if (!triggerCondition) return;

    const requestBody = {
      request_id: settings.request_id,
    };

    // Add handoff flag if specified
    if (isHandoff) {
      requestBody.handoff = true;
    }

    const handleStatusResponse = (result) => {
      if (!result) return true; // Continue polling if no result

      const {
        status,
        should_continue_polling: shouldContinuePolling,
        process_state: processState,
      } = result;

      if (customHandler) {
        const handlerResponse = customHandler(result, false);
        if (handlerResponse === false) {
          return false;
        }
      } else {
        if (processState === "COMPLETED") {
          if (status === "PASS") {
            dispatch({ type: "SET_STATUS", payload: "PASS" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            return false;
          }

          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }

        if (status === "PASS") {
          dispatch({ type: "SET_STATUS", payload: "PASS" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }

        if (status === "FAIL") {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }
      }

      if (shouldContinuePolling) {
        return true;
      }

      if (!customHandler && processState === "WAITING") {
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
        return false;
      }

      if (!customHandler && processState === "INITIALIZED") {
        // Keep polling when initialized
        return true;
      }

      if (!customHandler && processState === "HANDED_OFF") {
        // Keep polling when handed off to mobile
        return true;
      }

      return false;
    };

    const fetchStatusCheck = async () => {
      // Skip this request if another one is already in progress
      if (isRequestPendingRef.current) {
        return;
      }
      
      try {
        // Mark that we're starting a request
        isRequestPendingRef.current = true;
        
        const edgeStatusUrl = getEdgeStatusUrl();

        const response = await fetch(edgeStatusUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch status");
        }

        const result = await response.json();
        const shouldContinue = handleStatusResponse(result);
        
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      } catch (error) {
        // Continue silently without breaking the flow
        console.error("Edge status check error:", error);
      } finally {
        // Mark that the request has completed
        isRequestPendingRef.current = false;
      }
    };

    // Initial fetch with delay
    let timeoutId = setTimeout(() => {
      fetchStatusCheck();
      
      // Then set up the interval for subsequent fetches
      intervalId = setInterval(fetchStatusCheck, interval);
    }, interval); // Wait 2 seconds before first fetch
    
    let intervalId;

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      // Reset pending flag when unmounting
      isRequestPendingRef.current = false;
    };
  }, [
    triggerCondition,
    settings.request_id,
    dispatch,
    customHandler,
  ]);
};

export default useEdgeStatusCheck;
